import { useState } from "react";
import GameShell from "../GameShell";

/**
 * Diário de Bordo — CBCL / BASC-3 inspired weekly behavioral log.
 * 12 statements, 4-point Likert (Nunca/Às vezes/Muitas vezes/Sempre).
 * Tracks subscales: internalizing, externalizing, attention.
 */
export default function ShipLog() {
  return (
    <GameShell gameId="ship_log" title="Diário de Bordo">
      {({ onFinish }) => <Game onFinish={onFinish} />}
    </GameShell>
  );
}

const SCALE = [
  { v: 0, label: "Nunca" },
  { v: 1, label: "Às vezes" },
  { v: 2, label: "Muitas vezes" },
  { v: 3, label: "Sempre" },
];

const ITEMS = [
  { text: "Tem dificuldade para se concentrar em tarefas.", dim: "attention" },
  { text: "Termina o que começa.", dim: "attention", invert: true },
  { text: "Esquece coisas do dia a dia.", dim: "attention" },
  { text: "Se distrai com estímulos externos.", dim: "attention" },
  { text: "Fica triste ou desanimado(a).", dim: "internalizing" },
  { text: "Sente medo ou preocupação fora do comum.", dim: "internalizing" },
  { text: "Demonstra baixa autoestima.", dim: "internalizing" },
  { text: "Chora com facilidade.", dim: "internalizing" },
  { text: "Tem explosões de raiva.", dim: "externalizing" },
  { text: "Briga com outras crianças.", dim: "externalizing" },
  { text: "Desobedece adultos.", dim: "externalizing" },
  { text: "Tem dificuldade para esperar a vez.", dim: "externalizing" },
];

function Game({ onFinish }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [started] = useState(() => performance.now());

  const choose = (v) => {
    const it = ITEMS[idx];
    const w = it.invert ? 3 - v : v;
    const next = [...answers, { dim: it.dim, raw: v, weighted: w }];
    setAnswers(next);
    if (idx + 1 >= ITEMS.length) {
      const sum = (dim) =>
        next.filter((a) => a.dim === dim).reduce((s, a) => s + a.weighted, 0);
      const attention = sum("attention");
      const internalizing = sum("internalizing");
      const externalizing = sum("externalizing");
      const total = attention + internalizing + externalizing;
      const elapsed = performance.now() - started;
      onFinish({
        score: 70,
        duration_ms: Math.round(elapsed),
        metrics: {
          items: ITEMS.length,
          attention_score: attention,
          internalizing_score: internalizing,
          externalizing_score: externalizing,
          total_score: total,
          max_total: ITEMS.length * 3,
        },
      });
    } else {
      setIdx(idx + 1);
    }
  };

  const it = ITEMS[idx];

  return (
    <div className="mt-4 max-w-2xl mx-auto" data-testid="shiplog-area">
      <div className="text-center text-white drop-shadow mb-4">
        <div className="text-sm">📓 Avalie como foi a última semana.</div>
        <div className="text-xs mt-1 opacity-80">Item {idx + 1} / {ITEMS.length}</div>
      </div>
      <div className="kid-card pop-in" key={idx}>
        <div className="text-center text-5xl mb-3">📓</div>
        <div className="text-lg text-slate-800 font-bold text-center leading-snug mb-6">
          {it.text}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {SCALE.map((s) => (
            <button
              key={s.v}
              onClick={() => choose(s.v)}
              className="bg-sky-50 hover:bg-sky-100 active:scale-95 transition rounded-2xl py-3 px-2 font-semibold text-slate-700 border-2 border-transparent hover:border-sky-300"
              data-testid={`shiplog-val-${s.v}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
